import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { useTableData } from "../../report/hooks/hooks";
import { DelayForPareto } from "../models/DelayForPareto";
import { getDelaysByCode } from "../repositories/DelaysParetoReportRepository";

type Props = {
	item: DelayForPareto | null;
	startDate: Date;
	endDate: Date;
	delayAreaAssetId: number | null;
	includeMicroDelays: boolean;
	onClose: () => void;
};

export const DrillDownModal = ({
	item,
	startDate,
	endDate,
	delayAreaAssetId,
	includeMicroDelays,
	onClose,
}: Props) => {
	const [error, setError] = useState("");
	const { columns } = useTableData();
	const { defaultColDef } = useGridDefinitions({});

	const { data: rows = [], isLoading } = useQuery({
		queryKey: [
			"drillDownDelays",
			item?.DelayCodeId,
			startDate,
			endDate,
			delayAreaAssetId,
			includeMicroDelays,
		],
		queryFn: ({ signal }) =>
			getDelaysByCode(
				{
					startDate,
					endDate,
					delayCodeId: item?.DelayCodeId ?? null,
					delayAreaAssetId,
					includeMicroDelays,
				},
				signal,
			),
		enabled: item !== null,
		onError: (e: Error) => setError(e.message),
	});

	const title = item
		? item.DelayCodeName || item.DelayCategoryName || "Delay Events"
		: "Delay Events";

	return (
		<>
			<Dialog open={item !== null} onClose={onClose} maxWidth="xl" fullWidth>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					{title}
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Paper sx={{ height: "60vh", width: "100%" }} elevation={0}>
						<AgGridReact
							loading={isLoading}
							rowData={rows}
							columnDefs={columns}
							defaultColDef={defaultColDef}
							animateRows
							rowHeight={38}
							headerHeight={35}
							tooltipShowDelay={0}
							tooltipHideDelay={2000}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</DialogContent>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
